import { useState } from 'react';
import { CalendarCheck, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { ConfirmedSessionInfo } from '@/api/chat';

interface ConfirmedSessionsPanelProps {
  confirmedSessions: ConfirmedSessionInfo[];
}

export function ConfirmedSessionsPanel({ confirmedSessions }: ConfirmedSessionsPanelProps) {
  const [expanded, setExpanded] = useState(false);

  if (!confirmedSessions || confirmedSessions.length === 0) {
    return null;
  }

  const firstSession = confirmedSessions[0];
  const hasMultiple = confirmedSessions.length > 1;

  // Collapsed view - single line with first session preview
  if (!expanded) {
    return (
      <div 
        className="flex-shrink-0 border-b border-green-200 bg-green-50/50 px-3 py-2 cursor-pointer hover:bg-green-50 transition-colors"
        onClick={() => setExpanded(true)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <CalendarCheck className="h-4 w-4 text-green-600 flex-shrink-0" />
            <span className="text-xs font-medium text-green-700">
              {firstSession.timeDescription}
            </span>
            {firstSession.subject && (
              <span className="text-xs text-green-600 truncate">• {firstSession.subject}</span>
            )}
            {hasMultiple && (
              <span className="text-xs text-green-500 bg-green-100 px-1.5 py-0.5 rounded-full">
                +{confirmedSessions.length - 1} more
              </span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 text-green-500 flex-shrink-0 ml-2" />
        </div>
      </div>
    );
  }

  // Expanded view - show all sessions
  return (
    <div className="flex-shrink-0 border-b border-green-200 bg-green-50/50">
      {/* Collapsible header */}
      <div 
        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-green-50 transition-colors"
        onClick={() => setExpanded(false)}
      >
        <div className="flex items-center gap-2 text-xs font-medium text-green-700">
          <CalendarCheck className="h-4 w-4 text-green-600" />
          <span>Confirmed Sessions ({confirmedSessions.length})</span>
        </div>
        <ChevronUp className="h-4 w-4 text-green-500" />
      </div>
      
      {/* Session list */}
      <div className="px-3 pb-2 space-y-1.5">
        {confirmedSessions.map((session) => (
          <div
            key={session.sessionId}
            className="flex items-center gap-2 px-2.5 py-1.5 bg-white rounded-md border border-green-200 text-xs hover:border-green-300 transition-colors"
          >
            <Clock className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
            <span className="font-medium text-green-800">
              {session.timeDescription}
            </span>
            {session.subject && (
              <span className="text-green-600 truncate">• {session.subject}</span>
            )}
            {session.duration && (
              <span className="text-green-500 ml-auto flex-shrink-0">
                {session.duration}min
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
